import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useParams, Link } from 'react-router-dom';
import Header from './components/Header';
import GameSection from './components/GameSection';
import GameDetail from './components/GameDetail';
import { Game } from './types';
import { games, banner } from './data/games';
import NotFound from './components/NotFound';
import Footer from './components/Footer';
import SearchResults from './components/SearchResults';
import AboutUs from './components/AboutUs';
import ContactUs from './components/ContactUs';
import PrivacyPolicy from './components/PrivacyPolicy';
import { useRecentGames } from './hooks/useRecentGames';
import { useTranslation } from 'react-i18next';
import RecentPlayedList from './components/RecentPlayedList';

interface HomePageProps {
  onScroll: (position: number) => void;
}

function HomePage({ onScroll }: HomePageProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredGames, setFilteredGames] = useState<Game[]>(games);
  const { recentGames, addRecentGame } = useRecentGames();
  const { t } = useTranslation();

  useEffect(() => {
    const filtered = games.filter((game) => 
      game.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      game.description.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredGames(filtered);
  }, [searchQuery]);

  useEffect(() => {
    const handleScroll = () => {
      onScroll(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [onScroll]);

  return (
    <div className="flex flex-col min-h-screen">
      <Header onSearch={setSearchQuery} />
      
      <div className="flex-1 bg-[#1a1625] pt-8">
        <main className="container mx-auto px-4 py-8">
          {/* Recently Played Section */}
          {recentGames.length > 0 && (
            <div className="my-6 overflow-x-auto">
              <div className="flex items-center gap-4 min-w-0">
                <div className="flex-shrink-0 bg-[#1E1A2E] rounded-2xl w-[106px] h-[50px] flex items-center justify-between px-4">
                  <div className="flex flex-col justify-center flex-1">
                    <h2 className="text-sm font-medium text-white text-center">
                      {t('common.recently')}
                    </h2>
                    <h2 className="text-sm font-medium text-white text-center">
                      {t('common.played')}
                    </h2>
                  </div>
                  <svg 
                    viewBox="0 0 24 24" 
                    className="w-4 h-4 text-purple-400 ml-1"
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </div>

                <div className="flex gap-4">
                  {recentGames.map(game => (
                    <div key={game.id} className="relative group">
                      <Link 
                        to={`/game/${game.id}`}
                        className="block"
                      >
                        <div className="relative w-16 h-16">
                          <div className="absolute inset-0 rounded-2xl overflow-hidden">
                            <img
                              src={game.icon}
                              alt={game.title}
                              className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-110"
                            />
                          </div>
                          
                          <div className="absolute inset-0 rounded-2xl border-2 border-transparent group-hover:border-purple-500 transition-colors" />

                          <div 
                            className="absolute inset-x-0 bottom-0 rounded-b-2xl
                              opacity-0 translate-y-full group-hover:translate-y-0 group-hover:opacity-100
                              transition-all duration-200"
                          >
                            <div className="absolute left-1/2 -translate-x-1/2 bottom-0 bg-black/80 px-2 rounded-lg h-4 flex items-center justify-center">
                              <span className="text-[10px] text-white font-medium whitespace-nowrap">
                                {game.title}
                              </span>
                            </div>
                          </div>
                        </div>
                      </Link>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Hero Section - 根據 isActive 控制顯示 */}
          {banner.isActive && (
            <div className="relative rounded-2xl overflow-hidden bg-gradient-to-r from-purple-900 to-indigo-900 my-8 p-8">
              <div className="flex flex-col md:flex-row items-center justify-between">
                <div className="md:w-1/2 text-white z-10">
                  <h1 className="text-4xl md:text-6xl font-bold mb-4">{banner.title}</h1>
                  <p className="text-xl md:text-2xl mb-6">
                    {banner.description.split('\n').map((line, i) => (
                      <React.Fragment key={i}>
                        {line}
                        <br />
                      </React.Fragment>
                    ))}
                  </p>
                </div>
                <div className="md:w-1/2 flex justify-end">
                  <img
                    src={banner.imageUrl}
                    alt="Gaming"
                    className="w-64 h-64 object-cover rounded-lg"
                  />
                </div>
              </div>
              {/* Decorative Elements */}
              <div className="absolute top-4 right-4 w-8 h-8 bg-blue-500 rotate-45"></div>
              <div className="absolute bottom-4 left-4 w-8 h-8 bg-purple-500 rotate-45"></div>
            </div>
          )}

          <RecentPlayedList />

          <GameSection title="Top Games" games={filteredGames.slice(0, 4)} />
          <GameSection title="Popular Games" games={filteredGames.slice(4, 8)} />
          <GameSection title="More Games" games={filteredGames.slice(8)} />
        </main>
      </div>

      <Footer />
    </div>
  );
}

function GameDetailWrapper() {
  const { id } = useParams();
  const game = games.find(g => g.id === id);

  if (!game) {
    return <NotFound />;
  }

  return <GameDetail game={game} />;
}

function AppRoutes() {
  const [homeScrollPosition, setHomeScrollPosition] = useState(0);
  const location = useLocation();
  const prevPathRef = useRef(location.pathname);

  useEffect(() => {
    // 只有當從其他頁面返回首頁時才恢復滾動位置
    if (location.pathname === '/' && prevPathRef.current !== '/') {
      // 使用 setTimeout 確保 DOM 已經渲染完成
      setTimeout(() => {
        window.scrollTo(0, homeScrollPosition);
      }, 0);
    } else if (location.pathname !== '/') {
      // 非首頁時滾動到頂部
      window.scrollTo(0, 0);
    }
    
    // 更新前一個路徑
    prevPathRef.current = location.pathname;
  }, [location.pathname, homeScrollPosition]);

  return (
    <Routes>
      <Route 
        path="/" 
        element={
          <HomePage onScroll={(position) => setHomeScrollPosition(position)} />
        } 
      />
      <Route 
        path="/game/:id" 
        element={<GameDetailWrapper />} 
      />
      <Route 
        path="/search" 
        element={<SearchResults />} 
      />
      <Route path="/about" element={<AboutUs />} />
      <Route path="/contact" element={<ContactUs />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function App() {
  return (
    <Router basename="/">
      <AppRoutes />
    </Router>
  );
}

export default App;